import { useEffect, useRef, useState } from 'react'
import { api } from '../api/client'
import type { Supplier } from '../api/types'

/**
 * Ricerca del fornitore per nome o codice.
 *
 * Nessuno ricorda che Royal Canin è il fornitore <code>000355</code>: chiedere il codice
 * costringe a cercarlo altrove. Qui si digita quello che si ha in mente — il nome — e si sceglie
 * dall'elenco; il codice resta il valore trasmesso, invisibile a chi lo usa.
 *
 * La ricerca parte da due caratteri e attende una pausa nella digitazione: senza, ogni tasto
 * sarebbe una chiamata al gestionale.
 */
export function SupplierPicker({
  value,
  onChange,
  placeholder = 'Nome o codice fornitore',
  disabled,
}: {
  /** Codice del fornitore scelto, oppure stringa vuota. */
  value: string
  onChange: (code: string, supplier?: Supplier) => void
  placeholder?: string
  disabled?: boolean
}) {
  const [text, setText] = useState('')
  const [results, setResults] = useState<Supplier[]>()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [chosen, setChosen] = useState<Supplier>()

  const box = useRef<HTMLDivElement>(null)

  // Se il codice arriva dall'esterno (filtro ripristinato, azzeramento) l'etichetta si riallinea.
  useEffect(() => {
    if (!value) {
      setChosen(undefined)
      setText('')
      return
    }
    if (chosen?.code === value) return

    let cancelled = false
    api
      .get<Supplier>(`/suppliers/${encodeURIComponent(value)}`)
      .then((s) => {
        if (!cancelled) setChosen(s)
      })
      .catch(() => {
        // Codice non trovato: si mostra comunque quello digitato, senza inventare un nome.
        if (!cancelled) setChosen(undefined)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  // Ricerca ritardata: si interroga il gestionale quando l'utente smette di digitare.
  useEffect(() => {
    const q = text.trim()
    if (q.length < 2) {
      setResults(undefined)
      return
    }

    let cancelled = false
    setLoading(true)

    const timer = setTimeout(() => {
      api
        .get<Supplier[]>('/suppliers', { search: q, take: 25 })
        .then((data) => {
          if (!cancelled) {
            setResults(data)
            setOpen(true)
          }
        })
        .catch(() => {
          if (!cancelled) setResults([])
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
    }, 300)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [text])

  // Un clic fuori chiude l'elenco senza scegliere.
  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  const pick = (s: Supplier) => {
    setChosen(s)
    setText('')
    setResults(undefined)
    setOpen(false)
    onChange(s.code, s)
  }

  const clear = () => {
    setChosen(undefined)
    setText('')
    setResults(undefined)
    setOpen(false)
    onChange('')
  }

  // Fornitore già scelto: si mostra chi è, non il codice da decifrare.
  if (value && !open) {
    return (
      <div className="supplier-picker" ref={box}>
        <div className="supplier-chosen">
          <span className="supplier-chosen-name">{chosen?.name ?? value}</span>
          <span className="muted">{value}</span>
          {!disabled && (
            <button type="button" className="supplier-clear" onClick={clear} title="Togli il fornitore">
              ×
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="supplier-picker" ref={box}>
      <input
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        onFocus={() => results && setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false)
          // Un solo risultato: Invio lo sceglie, senza passare dal mouse.
          if (e.key === 'Enter' && results?.length === 1) {
            e.preventDefault()
            pick(results[0])
          }
        }}
      />

      {open && (
        <div className="supplier-results">
          {loading && <div className="muted supplier-hint">Cerco…</div>}

          {!loading && results?.length === 0 && (
            <div className="muted supplier-hint">Nessun fornitore trovato per «{text.trim()}».</div>
          )}

          {results?.map((s) => (
            <button key={s.code} type="button" className="supplier-item" onClick={() => pick(s)}>
              <span className="supplier-item-name">{s.name}</span>
              <span className="muted">{s.code}</span>
            </button>
          ))}
        </div>
      )}

      {!open && text.trim().length > 0 && text.trim().length < 2 && (
        <span className="field-hint">Scrivi almeno due caratteri.</span>
      )}
    </div>
  )
}
